import { useMemo, useState } from "react";
import type { DrawingRef } from "../../contracts/types";
import { analyzeDrawings, registerToText } from "../../contracts/analyzeDrawings";

// Analysis of the current drawing register: deterministic stats (discipline
// breakdown, duplicates, numbering gaps, incomplete entries) plus an optional
// AI coordination review.
export function DrawingAnalysisPanel({ drawings }: { drawings: DrawingRef[] }) {
  const a = useMemo(() => analyzeDrawings(drawings), [drawings]);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiText, setAiText] = useState<string | null>(null);
  const [aiErr, setAiErr] = useState<string | null>(null);

  async function aiReview() {
    setAiBusy(true);
    setAiErr(null);
    try {
      const res = await fetch("/api/draft-field", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          label: "Drawing register coordination review",
          current: registerToText(drawings),
          instruction:
            "Review this construction drawing register for completeness and coordination. In 3-6 short bullet points, note any missing disciplines or sheet types you would expect, likely coordination gaps, and concerns. Do not restate the list.",
          context: { disciplines: a.byDiscipline, total: a.total },
        }),
      });
      if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
      const data = (await res.json()) as { text?: string };
      setAiText(data.text || "");
    } catch (e: any) {
      setAiErr(`AI review unavailable (${String(e.message).slice(0, 80)}).`);
    } finally {
      setAiBusy(false);
    }
  }

  return (
    <div className="card analysis">
      <div className="an-top">
        <div className="an-total">{a.total}</div>
        <div className="an-total-lbl">sheet{a.total === 1 ? "" : "s"} in register</div>
      </div>

      {a.byDiscipline.length > 0 && (
        <div className="an-bars">
          {a.byDiscipline.map((d) => (
            <div className="an-bar" key={d.name}>
              <span className="an-bar-lbl">{d.name}</span>
              <span className="an-bar-track"><span className="an-bar-fill" style={{ width: `${a.total ? (d.count / a.total) * 100 : 0}%` }} /></span>
              <span className="an-bar-n">{d.count}</span>
            </div>
          ))}
        </div>
      )}

      <ul className="an-notes">
        {a.notes.map((n, i) => (
          <li key={i} className={/gap|duplicate|no title|empty/i.test(n) ? "warn" : /could not|no revision/i.test(n) ? "info" : "ok"}>{n}</li>
        ))}
      </ul>

      <div className="an-actions">
        <button onClick={aiReview} disabled={aiBusy || !a.total}>{aiBusy ? <span className="spinner dark-spin" /> : "✦ AI coordination review"}</button>
      </div>
      {aiErr && <div className="hint" style={{ color: "#a3402f" }}>{aiErr}</div>}
      {aiText && <div className="an-ai">{aiText.split(/\n+/).map((l, i) => <div key={i}>{l}</div>)}</div>}
    </div>
  );
}
